import { CONDITIONS } from "./constants";

/*
 * No price is invented for a condition the source did not quote.
 *
 * There used to be a table of multipliers here — Lightly Played 0.85, Damaged
 * 0.35 — that scaled every missing condition from Near Mint and tagged the
 * result "est." A tagged guess is still a guess, and on a Charizard it was
 * wrong by enough to matter: condition spreads are not a percentage. A one
 * dollar card that is damaged is still fifty cents; a five hundred dollar card
 * with a crease has lost most of itself. No flat number describes both, and
 * the sources this app reads quote one price per printing, which is in
 * practice Near Mint.
 *
 * So a condition with no quote has no price, and the screen asks for one.
 * Somebody holding the card can judge it; a constant cannot.
 */

/**
 * Where to see what copies of this card have actually sold for.
 *
 * Every condition below Near Mint on this screen is a multiplier on the one
 * price the source gives — an estimate, and the code above says so. What it
 * cannot say is what a played or damaged copy really fetches, because no
 * source publishes that: there is no Damaged market the way there is a Near
 * Mint one. Sold listings are the closest thing to an answer, and they are a
 * link away. No API, no key, no cost.
 */
/**
 * The card on TCGplayer, by search when there is no direct link.
 *
 * Pokemon results carry a product URL from the source; One Piece results and
 * anything in the collection do not. A search URL is the honest fallback: it
 * lands on the right card almost always, and on a short list of prints when
 * it does not, which is still the page somebody wanted.
 */
export function tcgplayerUrl(game, name, number) {
  const terms = [name, number].filter(Boolean).join(" ").trim();
  if (!terms) return null;
  const line = game === "onepiece" ? "one-piece-card-game" : "pokemon";
  return `https://www.tcgplayer.com/search/${line}/product?q=${encodeURIComponent(terms)}`;
}

export function soldListingsUrl(name, number, setName) {
  const terms = [name, number, setName].filter(Boolean).join(" ").trim();
  if (!terms) return null;
  const q = encodeURIComponent(terms);
  return `https://www.ebay.com/sch/i.html?_nkw=${q}&LH_Sold=1&LH_Complete=1`;
}

// The API nests prices as prices.variants[printing][conditionName], where the
// condition name carries the printing with it ("Near Mint Holofoil"). Flatten
// that to the plain condition names the UI works in, preferring the printing
// the API marks as primary.
function flattenVariantPrices(prices) {
  const out = {};
  const variants = prices?.variants;
  const names = variants && typeof variants === "object" ? Object.keys(variants) : [];
  const printing = names.length
    ? names.includes(prices.primaryPrinting) ? prices.primaryPrinting : names[0]
    : undefined;

  for (const [rawName, entry] of Object.entries((printing && variants[printing]) || {})) {
    const price = Number(entry?.price);
    if (!Number.isFinite(price)) continue;
    // "Near Mint Holofoil" -> "Near Mint"; longest match first so
    // "Moderately Played" wins over any shorter prefix.
    const match = CONDITIONS.slice()
      .sort((a, b) => b.length - a.length)
      .find((cond) => rawName.startsWith(cond));
    if (match && out[match] == null) out[match] = { price, estimated: false, printing };
  }

  /* The market price is Near Mint's if nothing said so explicitly — that is
     what every source here means by "market". Nothing else is filled in. */
  const base = Number(prices?.market);
  if (!out["Near Mint"] && Number.isFinite(base) && base > 0) {
    out["Near Mint"] = { price: base, estimated: false, printing };
  }
  return out;
}

// The API answers with two different shapes: a search returns data as an array,
// while a lookup by tcgPlayerId returns data as a single card object. Calling
// .map on the second one throws, so always come through here.
export function cardsFromPayload(payload) {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") return [data];
  return [];
}

// Normalize API response into a consistent card shape
export const normalizeFromPPT = (c) => ({
  id: c.tcgPlayerId || c.id || `${c.name}-${c.cardNumber}`,
  name: c.name,
  number: c.cardNumber,
  imageUrl: c.imageUrl,
  setId: c.setId || null,
  setName: c.setName || null,
  prices: flattenVariantPrices(c?.prices),
  marketPrice: c?.prices?.market ?? null,
  externalUrl: c.priceGuideUrl || c.tcgPlayerUrl || null,
  soldUrl: soldListingsUrl(c.name, c.cardNumber, c.setName),
});

// Try to extract a numeric price from a grade data entry.
// Handles: plain numbers, objects with price fields, arrays of sales.
const extractGradePrice = (gradeData) => {
  if (gradeData == null) return null;
  if (typeof gradeData === "number" && gradeData > 0) return gradeData;
  if (typeof gradeData === "string") {
    const n = parseFloat(gradeData);
    return n > 0 ? n : null;
  }
  if (typeof gradeData !== "object") return null;

  // Direct price fields
  const priceFields = ["marketPrice7Day", "marketPriceMedian7Day", "medianPrice",
                       "marketPrice", "avgPrice", "price", "averagePrice", "average", "avg",
                       "median", "value", "soldPrice"];
  for (const f of priceFields) {
    if (gradeData[f] != null) {
      const n = Number(gradeData[f]);
      if (n > 0) return n;
    }
  }

  // Array of sales — average last 5
  const salesArr = Array.isArray(gradeData) ? gradeData
    : Array.isArray(gradeData.sales) ? gradeData.sales
    : Array.isArray(gradeData.recentSales) ? gradeData.recentSales
    : null;
  if (salesArr && salesArr.length > 0) {
    const prices = salesArr
      .map((s) => Number(s?.price || s?.soldPrice || s?.salePrice || s?.amount || 0))
      .filter((p) => p > 0)
      .slice(0, 5);
    if (prices.length > 0) return prices.reduce((a, b) => a + b, 0) / prices.length;
  }
  return null;
};

// Recursively search an object for anything that looks like graded price data.
// Returns { 1: price, ..., 10: price } or null.
const deepSearchGradedPrices = (obj, depth = 0) => {
  if (!obj || typeof obj !== "object" || depth > 6) return null;

  const ALL_GRADES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const result = {};

  // Check if this object itself is keyed by grade numbers (e.g. { "9": ..., "10": ... })
  const numericKeys = Object.keys(obj).filter((k) => /^\d+$/.test(k) && Number(k) >= 1 && Number(k) <= 10);
  if (numericKeys.length >= 2) {
    for (const grade of ALL_GRADES) {
      const val = obj[String(grade)] || obj[grade];
      const price = extractGradePrice(val);
      if (price != null) result[grade] = price;
    }
    if (Object.keys(result).length >= 2) return result;
  }

  // Keyed by grade name, which is the shape PPT actually uses:
  //   ebay.salesByGrade = { psa10, psa9, psa8, ..., psa1, bgs9, cgc8_5, ungraded }
  // Only PSA keys are taken; the regex ignores bgs/cgc/ungraded siblings. This
  // used to keep grades 9 and 10 and silently drop 1-8, so cards with real sales
  // at lower grades showed nothing.
  const psaKeys = Object.keys(obj).filter((k) => /^psa\d+$/i.test(k));
  if (psaKeys.length >= 2) {
    for (const key of psaKeys) {
      const grade = Number(key.match(/(\d+)$/)[1]);
      if (!ALL_GRADES.includes(grade)) continue;
      const price = extractGradePrice(obj[key]);
      if (price != null) result[grade] = price;
    }
    if (Object.keys(result).length >= 1) return result;
  }

  // Check if it's an array of sale objects with grade + price
  if (Array.isArray(obj)) {
    const withGrade = obj.filter((s) => s && (s.grade != null || s.psaGrade != null));
    if (withGrade.length >= 2) {
      // Filter to PSA if grader field exists, otherwise use all
      const hasPsaFilter = withGrade.some((s) => s.grader || s.company);
      const sales = hasPsaFilter
        ? withGrade.filter((s) => /psa/i.test(s.grader || s.company || ""))
        : withGrade;
      for (const grade of ALL_GRADES) {
        const gradeSales = sales
          .filter((s) => Number(s.grade ?? s.psaGrade) === grade)
          .map((s) => Number(s.price || s.soldPrice || s.salePrice || s.amount || 0))
          .filter((p) => p > 0)
          .slice(0, 5);
        if (gradeSales.length > 0) {
          result[grade] = gradeSales.reduce((a, b) => a + b, 0) / gradeSales.length;
        }
      }
      if (Object.keys(result).length >= 1) return result;
    }
  }

  // Recurse into child keys, prioritizing eBay/PSA/grade-related names
  const keys = Object.keys(obj);
  const priority = /ebay|psa|cgc|bgs|grade|graded|slab|sold|sale|auction/i;
  const sorted = [...keys].sort((a, b) => {
    const ap = priority.test(a) ? 0 : 1;
    const bp = priority.test(b) ? 0 : 1;
    return ap - bp;
  });
  for (const key of sorted) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      const found = deepSearchGradedPrices(obj[key], depth + 1);
      if (found) return found;
    }
  }
  return null;
};

// Extract PSA average prices from eBay graded sales data.
// Returns { 1: avgPrice, 2: avgPrice, ..., 10: avgPrice } or null.
// Performs a deep recursive search through the entire response to find grade/price data.
export const extractPsaPrices = (ebayData, fullResponse = null) => {
  if (!ebayData && !fullResponse) {
    return null;
  }


  // First try the ebayData directly (original behavior)
  if (ebayData && typeof ebayData === "object") {
    const result = deepSearchGradedPrices(ebayData);
    if (result) {
      return result;
    }
  }

  // If ebayData yielded nothing and we have the full API response, search the whole thing
  if (fullResponse && typeof fullResponse === "object") {
    const result = deepSearchGradedPrices(fullResponse);
    if (result) {
      return result;
    }
  }

  return null;
};

// Helper: does a user-entered number like "199"
// match an API cardNumber like "199/165"?
// Leading zeros are a printing convention, not part of the number: a card
// printed 020 is listed as 20 by some sources and 020 by others, and typing
// what's on the card must match either.
const withoutLeadingZeros = (s) => s.replace(/^0+(?=\d)/, "");

export const cardNumberMatches = (userNumber, apiCardNumber) => {
  if (!userNumber || !apiCardNumber) return false;
  const trimmedUser = String(userNumber).trim();
  const normalizedApi = String(apiCardNumber).trim();
  if (!trimmedUser || !normalizedApi) return false;
  if (normalizedApi === trimmedUser) return true;
  const prefix = normalizedApi.split(/[^\d]/)[0];
  if (prefix === trimmedUser) return true;
  return withoutLeadingZeros(prefix) === withoutLeadingZeros(trimmedUser);
};

// Format currency as US dollars
export const formatUSD = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "No price";
};

// The eBay payload nests graded sales under one of several possible keys, and
// the same four-way guess was copy-pasted into every caller. One place now.
export const psaPricesFromEbayPayload = (payload) => {
  const card = payload?.data || payload;
  const block =
    card?.ebay || card?.ebaySales || card?.ebayGraded || card?.gradedSales || card?.graded || null;
  return extractPsaPrices(block, payload);
};